import React from 'react';
import styled from 'styled-components';
import Loader from 'components/_UI/Loader';
import Switch from 'components/_UI/Switch';
import Text from 'components/_UI/Text';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import usePanelPermissions from 'hooks/usePanelPermissions';
import useTeamPermissionCatalog from './useTeamPermissionCatalog';

/** Atalhos para montar o conjunto de permissões de um papel comum. */
const PRESETS = [
    { label: 'Acesso total', pick: (all) => all },
    { label: 'Somente leitura', pick: (all) => all.filter((key) => key.endsWith('.view')) },
    { label: 'Portaria', pick: () => ['ticket', 'product', 'participants.view'] },
    { label: 'Financeiro', pick: () => ['dashboard.view', 'orders.view', 'financial.view'] },
    { label: 'Marketing', pick: () => ['customers.view', 'campaigns.view', 'campaigns.send', 'coupons.view', 'coupons.manage'] },
];

/** Soma ao conjunto o que cada permissão marcada já traz junto. */
export function withImplied(selected, implications) {
    const result = new Set(selected);

    selected.forEach((key) => (implications[key] || []).forEach((implied) => result.add(implied)));

    return [...result];
}

/**
 * Interruptores das permissões do painel, agrupados como o menu do painel.
 * Marcar uma permissão marca também o que ela inclui (quem edita já vê); e
 * desmarcar uma permissão desmarca as que dependem dela. Quem gerencia
 * equipes por uma equipe só pode entregar o que ele mesmo tem.
 */
export default function TeamPermissionsPicker({ value, onChange }) {
    const { loading, groups, implications } = useTeamPermissionCatalog();
    const { can } = usePanelPermissions();

    const allKeys = groups.flatMap((group) => group.permissions.map((permission) => permission.key));
    const allowedKeys = allKeys.filter((key) => can(key));

    function toggle(key, checked) {
        if (checked) {
            onChange(withImplied([...value, key], implications));
            return;
        }

        const dependents = allKeys.filter((other) => (implications[other] || []).includes(key));

        onChange(value.filter((item) => item !== key && !dependents.includes(item)));
    }

    function applyPreset(preset) {
        onChange(withImplied(preset.pick(allowedKeys).filter((key) => allowedKeys.includes(key)), implications));
    }

    if (loading) {
        return (
            <FullyCentered height='80px'>
                <Loader />
            </FullyCentered>
        );
    }

    return (
        <Column>
            <PresetsRow>
                <PresetsLabel>Atalhos:</PresetsLabel>
                {PRESETS.map((preset) => (
                    <PresetChip key={preset.label} type='button' onClick={() => applyPreset(preset)}>
                        {preset.label}
                    </PresetChip>
                ))}
                <PresetChip type='button' onClick={() => onChange([])}>Limpar</PresetChip>
            </PresetsRow>

            <GroupsScroll>
            {groups.map((group) => (
                <Group key={group.key}>
                    <GroupTitle>{group.label}</GroupTitle>

                    {group.permissions.map((permission) => (
                        <Row key={permission.key}>
                            <Switch
                                id={`team-permission-${permission.key}`}
                                label={permission.label}
                                marginTop='10px'
                                checked={value.includes(permission.key)}
                                disabled={!can(permission.key)}
                                onChange={(checked) => toggle(permission.key, checked)}
                            />
                            {permission.sensitive && <SensitiveBadge>Sensível</SensitiveBadge>}
                        </Row>
                    ))}
                </Group>
            ))}
            </GroupsScroll>

            <Text color={colors.nightLight} marginTop={'12px'}>
                "Sensível" é o que mexe em dinheiro ou em quem tem acesso ao painel — libere só para quem você confia.
            </Text>
        </Column>
    );
}

const PresetsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
`;

const PresetsLabel = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
`;

const PresetChip = styled.button`
    padding: 4px 12px;
    border-radius: 999px;
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '40')};
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: ${fonts.sizes.small};
    cursor: pointer;

    &:hover {
        background: ${panelAccentAlpha(colors.occasionalPurple, '22')};
    }
`;

/* A lista é longa: rola por dentro para o modal caber em qualquer tela. */
const GroupsScroll = styled.div`
    max-height: min(340px, 42vh);
    overflow-y: auto;
    margin-top: 10px;
    padding: 0 10px 8px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
`;

const Group = styled.div`
    margin-top: 4px;
    padding-top: 12px;

    & + & {
        margin-top: 12px;
        border-top: 1px solid ${colors.neutralDark};
    }
`;

const GroupTitle = styled.h5`
    margin: 0;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SensitiveBadge = styled.span`
    margin-top: 10px;
    padding: 1px 8px;
    border-radius: 999px;
    background: #FEF3C7;
    color: #92400E;
    font-size: 11px;
    font-weight: 600;
`;
